import template from './outstanding_data_report.html';
import * as Utils from '../../lib/savanna_utils.js';
import tr from '../../lib/default-translator.js';

class OutstandingDataViewModel
{
	constructor (page)
	{
		this.page = page;

		// Initialize with empty structure instead of null
		this.employee_missing_data = ko.observable({
			e_missing_info: [],
			field_names_list: [],
			status: 'OK'
		});
		this.all_employee_data = ko.observableArray([]); // Store all employee data
		this.show_export_table = ko.observable(false);
		this.include_default = ko.observable(true);
		this.show_correct_values = ko.observable(false);
		this.show_hint = ko.observable(true);
		this.show_no_data_message = ko.observable(false);
		this.show_download_report = ko.observable(false);

		// Pagination properties
		this.current_page_number = ko.observable(1);
		this.current_page_size = ko.observable(10);
		this.page_count = ko.observable(1);
		this.pagination_summary = ko.observable('');
		this.number_of_records = ko.observableArray([10, 20, 50, 100]);
		this.selected_number_of_records = ko.observable(10);
		this.total_employees = ko.observable(0);

		this.current_department_id = ko.observable();
		this.selected_department = ko.observable();

		this.selected_department.subscribe((newValue) => {
			this.all_employee_data([]);
			this.employee_missing_data({
				e_missing_info: [],
				field_names_list: [],
				status: 'OK'
			});
			this.show_hint(true);
			this.show_no_data_message(false);
			this.current_page_number(1); // Reset to first page

			if (newValue != null)
				this.current_department_id(newValue.department_id);
			else
				this.current_department_id(null);
		});

		this.selected_number_of_records.subscribe((newVal) => {
			this.current_page_size(newVal);
			this.current_page_number(1); // Reset to first page
			if (this.all_employee_data().length > 0) {
				this.updatePaginatedView();
			}
		});

		this.current_page_number.subscribe(() => {
			if (this.all_employee_data().length > 0) {
				this.updatePaginatedView();
			}
		});
	}

	page_click(page_number)
	{
		this.current_page_number(page_number);
	}

	updatePaginatedView()
	{
		const allData = this.all_employee_data();
		const pageSize = this.current_page_size();
		const currentPage = this.current_page_number();
		
		// Calculate pagination for employees
		const startIndex = (currentPage - 1) * pageSize;
		const endIndex = startIndex + pageSize;
		
		// Slice the employee array
		const paginatedEmployees = allData.slice(startIndex, endIndex);
		
		// Update the observable with paginated data - need to map to observables
		const mappedData = {
			e_missing_info: paginatedEmployees.map(emp => ko.mapping.fromJS(emp)),
			field_names_list: this.all_employee_data.field_names_list || [],
			status: 'OK'
		};
		
		this.employee_missing_data(mappedData);
		
		// Update pagination info
		const totalEmployees = allData.length;
		this.total_employees(totalEmployees);
		this.page_count(Math.ceil(totalEmployees / pageSize));
		
		const start = startIndex + 1;
		const end = Math.min(endIndex, totalEmployees);
		this.pagination_summary(`Showing ${start} to ${end} of ${totalEmployees} employees`);
	}

	back_to_top()
	{
		document.body.scrollTop = 0; // For Safari
		document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
	}
}

class OutstandingDataPage
{
	constructor (bindings)
	{
		this.bindings = bindings;
		this.viewModel = new OutstandingDataViewModel(this);
	}

	async init()
	{
		// check access to page
		if (await Utils.redirectOnAccessDenied('OutstandingDataReport'))
			return;

		document.title = tr('Outstanding Employee Information');
		window.current_page_title(tr('Outstanding Employee Information'));
		this.viewModel.show_hint(true);
	}

	updateData()
	{
		if (this.viewModel.current_department_id() != undefined)
		{
			this.viewModel.show_hint(false);
			this.update_report_data();
		}
	}

	async update_report_data()
	{
		let data = {
			department_id: this.viewModel.current_department_id()
		};

		let res = await Grape.fetches.postJSON('/reports/outstanding_data_report', data);

		if (res)
		{
			// Store the raw data (not mapped yet)
			const rawEmployeeData = res.e_missing_info || [];
			this.viewModel.all_employee_data(rawEmployeeData);
			this.viewModel.all_employee_data.field_names_list = res.field_names_list || [];
			
			console.log('Total employees loaded:', rawEmployeeData.length);
			
			// Reset to first page and update view
			this.viewModel.current_page_number(1);
			this.viewModel.updatePaginatedView();
			
			this.viewModel.show_hint(false);
			
			if (rawEmployeeData.length > 0)
			{
				this.viewModel.show_download_report(true);
				this.viewModel.show_no_data_message(false);
			}
			else
			{
				this.viewModel.show_download_report(false);
				this.viewModel.show_no_data_message(true);
			}
		}
	}

	show_report_help()
	{
		Grape.dialog.open('OutstandingDataHelp', { 'employee_id': this.bindings.id }, {});
	}

	async download_clicked(v, e)
	{
		try {
			// Use all employee data for download
			const allData = this.viewModel.all_employee_data();
			
			if (allData && allData.length > 0)
			{
				// Create a temporary full dataset for export
				const exportData = {
					e_missing_info: allData,
					field_names_list: this.viewModel.all_employee_data.field_names_list || []
				};

				// Temporarily update the observable with all data (mapped)
				let original_data = this.viewModel.employee_missing_data();
				this.viewModel.employee_missing_data(ko.mapping.fromJS(exportData));

				// Wait for DOM to update
				await new Promise(resolve => setTimeout(resolve, 100));

				let csv_string = await Utils.table_to_csv(document.getElementById('outstanding_data_report'));

				// Restore original paginated data
				this.viewModel.employee_missing_data(original_data);

				if (csv_string === null || csv_string === undefined || csv_string === '')
					return;

				let blob = new Blob([csv_string], { type: 'text/csv;charset=utf-8;' });
				let link = document.createElement('a');

				link.href = URL.createObjectURL(blob);
				link.setAttribute('download', 'outstanding_data_report.csv');

				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
			}
			else
			{
				Grape.alerts.alert({ 
					type: 'error', 
					title: tr('No Data'), 
					message: tr('There is no data to download.') 
				});
			}
		}
		catch (error)
		{
			console.error('Error downloading report:', error);
			Grape.alerts.alert({ 
				type: 'error', 
				title: tr('Error'), 
				message: tr('An error occurred while downloading the report.') 
			});
		}
	}
}

export default {
	route: '[/]report/outstanding_data',
	page_class: OutstandingDataPage,
	template: template,
	template_filename: 'translation/outstanding_data_report'
};